/**
 * SKU Service
 * Handles all SKU-related API calls for admin management
 */

import { apiClient, ApiResponse } from './api.client';
import { unwrapNamedList } from './api.unwrap';
import { SKU, CreateSKURequest, UpdateSKURequest } from '@creatoros/shared';

/**
 * SKU with user count (returned from list/get endpoints)
 */
export interface SKUWithUserCount extends SKU {
  userCount: number;
}

/**
 * User on SKU response
 */
export interface SKUUser {
  id: string;
  email: string;
  displayName: string;
  skuId?: string;
  subscriptionTier?: string;
  status: string;
  createdAt: string;
}

/**
 * SKU list response
 */
export interface SKUListResponse {
  skus: SKUWithUserCount[];
  total: number;
}

/**
 * SKU users response
 */
export interface SKUUsersResponse {
  users: SKUUser[];
  total: number;
  continuationToken?: string;
}

/**
 * SKU Service Class
 */
class SKUService {
  private readonly basePath = '/api/management/skus';

  /**
   * List all SKUs with optional filtering
   * @param params - Filter options
   * @returns List of SKUs
   */
  async list(params?: {
    isActive?: boolean;
    isPublic?: boolean;
    includeUserCount?: boolean;
  }): Promise<SKUListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.isActive !== undefined) {
      queryParams.set('isActive', String(params.isActive));
    }
    if (params?.isPublic !== undefined) {
      queryParams.set('isPublic', String(params.isPublic));
    }
    if (params?.includeUserCount) {
      queryParams.set('includeUserCount', 'true');
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await apiClient.get(url);
    const envelope = response.data;

    if (!envelope?.success) {
      throw new Error('Failed to fetch SKUs');
    }

    const raw = envelope.data;
    // Azure: { skus, total } | CF: { items } | flat array
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray(raw.skus)) {
      return {
        skus: raw.skus,
        total: typeof raw.total === 'number' ? raw.total : raw.skus.length,
      };
    }

    const skus = unwrapNamedList<SKUWithUserCount>(envelope, 'skus');
    return {
      skus,
      total:
        typeof envelope.total === 'number'
          ? envelope.total
          : typeof raw?.count === 'number'
            ? raw.count
            : skus.length,
    };
  }

  /**
   * Get a single SKU by ID
   * @param id - SKU ID
   * @returns SKU with user count
   */
  async getById(id: string): Promise<SKUWithUserCount> {
    const response = await apiClient.get<ApiResponse<SKUWithUserCount>>(`${this.basePath}/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch SKU');
  }

  /**
   * Create a new SKU
   * @param data - SKU creation data
   * @returns Created SKU
   */
  async create(data: CreateSKURequest): Promise<SKU> {
    const response = await apiClient.post<ApiResponse<SKU>>(this.basePath, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to create SKU');
  }

  /**
   * Update an existing SKU
   * @param id - SKU ID
   * @param data - SKU update data
   * @returns Updated SKU
   */
  async update(id: string, data: UpdateSKURequest): Promise<SKU> {
    const response = await apiClient.patch<ApiResponse<SKU>>(`${this.basePath}/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to update SKU');
  }

  /**
   * Deactivate a SKU (soft delete)
   * @param id - SKU ID
   * @returns Deactivated SKU
   */
  async deactivate(id: string): Promise<SKU> {
    const response = await apiClient.delete<ApiResponse<SKU>>(`${this.basePath}/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to deactivate SKU');
  }

  /**
   * Get users on a specific SKU
   * @param id - SKU ID
   * @param params - Pagination options
   * @returns Users on SKU
   */
  async getUsers(
    id: string,
    params?: { limit?: number; continuationToken?: string }
  ): Promise<SKUUsersResponse> {
    const queryParams = new URLSearchParams();

    if (params?.limit) {
      queryParams.set('limit', String(params.limit));
    }
    if (params?.continuationToken) {
      queryParams.set('continuationToken', params.continuationToken);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.basePath}/${id}/users?${queryString}`
      : `${this.basePath}/${id}/users`;

    const response = await apiClient.get(url);
    const envelope = response.data;

    if (!envelope?.success) {
      throw new Error('Failed to fetch users on SKU');
    }

    const raw = envelope.data;
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray(raw.users)) {
      return {
        users: raw.users,
        total: typeof raw.total === 'number' ? raw.total : raw.users.length,
        continuationToken: raw.continuationToken ?? envelope.continuationToken,
      };
    }

    const users = unwrapNamedList<SKUUser>(envelope, 'users');
    return {
      users,
      total: users.length,
      continuationToken: envelope.continuationToken,
    };
  }
}

/**
 * Export singleton instance
 */
export const skuService = new SKUService();

/**
 * Export default
 */
export default skuService;
